import type { ItemStockSummary } from '@erp/shared';
import type { TFunction } from 'i18next';
import { AlertTriangle, ArrowDownLeft, ArrowUpRight, Clock, type LucideIcon, Warehouse as WarehouseIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '../../../components/shadcn/index.js';
import { cn } from '../../../lib/cn.js';
import { formatRelative } from '../../../lib/intl.js';
import { formatMoney } from '../../../lib/money.js';

import { fmtQty, packBreakdown, tint } from './profileFormat.js';

/** A card's small upper-case heading, with an optional icon and trailing slot. */
export function SectionTitle({ icon: Icon, children, end }: {
  icon?: LucideIcon;
  children: ReactNode;
  end?: ReactNode;
}): JSX.Element {
  return (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <h2 className="flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide text-fg-muted">
        {Icon && <Icon className="h-4 w-4" aria-hidden />}
        {children}
      </h2>
      {end}
    </div>
  );
}

/**
 * A stock quantity, coloured by what it means: below zero is a problem and is
 * shown as one (red, warning icon), zero is muted, anything else is plain.
 * `dir="ltr"` keeps the minus sign in front of the digits on an RTL page.
 */
export function QtyText({ value, unit, className, strong }: {
  value: string | number;
  unit?: string | null;
  className?: string;
  strong?: boolean;
}): JSX.Element {
  const { i18n, t } = useTranslation('items');
  const n = Number(value);
  const negative = n < 0;
  return (
    <span
      className={cn(
        'inline-flex items-baseline gap-1 font-mono tabular-nums',
        strong && 'font-semibold',
        negative ? 'text-danger' : n === 0 ? 'text-fg-subtle' : 'text-fg',
        className,
      )}
      title={negative ? t('profile.stock.negativeHint', { defaultValue: 'Below zero — more was sold or moved out than was received.' }) : undefined}
    >
      {negative && <AlertTriangle className="h-3.5 w-3.5 self-center" aria-hidden />}
      <span dir="ltr">{fmtQty(value, i18n.language)}</span>
      {unit ? <span className="font-sans text-xs font-normal text-fg-muted">{unit}</span> : null}
    </span>
  );
}

/** The text of a PackHint, for places that take a string (a KPI card's hint). */
export function packLabel(
  t: TFunction,
  quantity: string | number,
  pack: { name: string; factor: number } | null,
  baseUnit: string,
  locale: string,
): string | null {
  const b = packBreakdown(quantity, pack);
  if (!b || !pack || b.packs === 0) return null;
  const packs = fmtQty(b.packs, locale);
  const rest = fmtQty(b.rest, locale);
  const text = b.rest > 0
    ? t('profile.packs.mixed', { defaultValue: '≈ {{packs}} {{pack}} + {{rest}} {{base}}', packs, pack: pack.name, rest, base: baseUnit })
    : t('profile.packs.exact', { defaultValue: '= {{packs}} {{pack}}', packs, pack: pack.name });
  return b.negative ? `− ${text}` : text;
}

/** "≈ 68 Carton + 28 pcs" — how a keeper counts the same quantity. */
export function PackHint({ quantity, pack, baseUnit, className }: {
  quantity: string | number;
  pack: { name: string; factor: number } | null;
  baseUnit: string;
  className?: string;
}): JSX.Element | null {
  const { t, i18n } = useTranslation('items');
  const text = packLabel(t, quantity, pack, baseUnit, i18n.language);
  if (!text) return null;
  return <span className={cn('text-xs text-fg-muted', Number(quantity) < 0 && 'text-danger', className)}>{text}</span>;
}

/** Money in several currencies, never added together: "$1,200 · 4,500,000 IQD". */
export function moneyList(list: Array<{ currencyCode: string; amount?: string; value?: string }>, locale: string): string {
  if (list.length === 0) return '—';
  return list.map((m) => formatMoney(m.amount ?? m.value ?? '0', { locale, currency: m.currencyCode })).join(' · ');
}

/**
 * One card per warehouse — how much of this product each branch holds, what
 * moved there lately, and when. Every active warehouse appears, zeros and all;
 * a negative branch is tinted red so it cannot be missed. With `onSelect`, a
 * card filters whatever sits below it (the movements list) to that branch.
 */
export function BranchStockCards({ summary, baseUnit, pack, canCost, selected, onSelect, compact }: {
  summary: ItemStockSummary;
  baseUnit: string;
  pack: { name: string; factor: number } | null;
  canCost: boolean;
  selected?: string;
  onSelect?: (warehouseId: string) => void;
  compact?: boolean;
}): JSX.Element {
  const { t, i18n } = useTranslation('items');
  const rel = (iso: string | null): string => (iso ? formatRelative(iso, { locale: i18n.language }) : t('profile.never', { defaultValue: 'never' }));
  const totalNegative = Number(summary.totals.quantity) < 0;

  const card = (key: string, body: ReactNode, opts: { negative: boolean; active: boolean; onClick?: () => void; label: string }): JSX.Element => {
    const cls = cn(
      'relative flex h-full flex-col gap-1.5 rounded-xl border p-3 text-start transition-[border-color,box-shadow,transform] duration-150',
      opts.negative ? 'border-danger' : 'border-border',
      opts.active ? 'shadow-md ring-2 ring-brand' : 'shadow-sm',
      opts.onClick && 'hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
    );
    const style = opts.negative ? tint('danger', 8) : undefined;
    return opts.onClick ? (
      <button key={key} type="button" className={cls} style={style} onClick={opts.onClick} aria-pressed={opts.active} aria-label={opts.label}>
        {body}
      </button>
    ) : (
      <div key={key} className={cls} style={style}>{body}</div>
    );
  };

  return (
    <div className={cn('grid gap-3', compact ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4')}>
      {/* All branches together — first, and the "clear the filter" card. */}
      {card('__all__', (
        <>
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-bold uppercase tracking-wide text-fg-muted">{t('profile.stock.allBranches', { defaultValue: 'All branches' })}</span>
            <span className="text-[11px] text-fg-subtle">{t('profile.stock.branchCount', { defaultValue: 'Branches: {{n}}', n: summary.warehouses.length })}</span>
          </div>
          <QtyText value={summary.totals.quantity} unit={baseUnit} strong className="text-2xl" />
          <PackHint quantity={summary.totals.quantity} pack={pack} baseUnit={baseUnit} />
          {!compact && (
            <InOut periodIn={summary.totals.periodIn} periodOut={summary.totals.periodOut} days={summary.days} />
          )}
          {summary.totals.negativeWarehouses > 0 && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-danger">
              <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
              {t('profile.stock.negativeBranches', { defaultValue: 'Branches below zero: {{n}}', n: summary.totals.negativeWarehouses })}
            </span>
          )}
        </>
      ), {
        negative: totalNegative,
        active: onSelect ? !selected : false,
        onClick: onSelect ? () => onSelect('') : undefined,
        label: t('profile.stock.allBranches', { defaultValue: 'All branches' }),
      })}
      {summary.warehouses.map((w) => {
        const negative = Number(w.quantity) < 0;
        const value = canCost && w.avgCost && w.avgCostCurrencyCode
          ? formatMoney(String(Number(w.quantity) * Number(w.avgCost)), { locale: i18n.language, currency: w.avgCostCurrencyCode })
          : null;
        return card(w.warehouseId, (
          <>
            <div className="flex items-start justify-between gap-2">
              <span className="flex min-w-0 items-center gap-1.5">
                <WarehouseIcon className="h-3.5 w-3.5 flex-none text-fg-muted" aria-hidden />
                <span className="truncate text-sm font-semibold text-fg">{w.name}</span>
              </span>
              <span className="flex-none font-mono text-[11px] text-fg-subtle">{w.code}</span>
            </div>
            <QtyText value={w.quantity} unit={baseUnit} strong className="text-xl" />
            <PackHint quantity={w.quantity} pack={pack} baseUnit={baseUnit} />
            {!compact && <InOut periodIn={w.periodIn} periodOut={w.periodOut} days={summary.days} />}
            <div className="mt-auto flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5 text-[11px] text-fg-muted">
              <span className="inline-flex items-center gap-1" title={w.lastMovementAt ?? undefined}>
                <Clock className="h-3 w-3" aria-hidden />
                {t('profile.stock.lastMoved', { defaultValue: 'Moved {{when}}', when: rel(w.lastMovementAt) })}
              </span>
              {value && <span className="font-mono tabular-nums text-fg">{value}</span>}
            </div>
            {!w.isActive && (
              <span className="text-[11px] font-semibold text-warning">{t('profile.stock.inactiveWarehouse', { defaultValue: 'Inactive warehouse' })}</span>
            )}
          </>
        ), {
          negative,
          active: selected === w.warehouseId,
          onClick: onSelect ? () => onSelect(w.warehouseId) : undefined,
          label: w.name,
        });
      })}
    </div>
  );
}

/** "↓ +51 in · ↑ −3 out · 30 days". */
function InOut({ periodIn, periodOut, days }: { periodIn: string; periodOut: string; days: number }): JSX.Element {
  const { t, i18n } = useTranslation('items');
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
      <span className="inline-flex items-center gap-0.5 text-success" title={t('profile.stock.inTitle', { defaultValue: 'Came in over the last {{days}} days', days })}>
        <ArrowDownLeft className="h-3.5 w-3.5" aria-hidden />
        <span dir="ltr" className="font-mono tabular-nums">+{fmtQty(periodIn, i18n.language)}</span>
      </span>
      <span className="inline-flex items-center gap-0.5 text-danger" title={t('profile.stock.outTitle', { defaultValue: 'Went out over the last {{days}} days', days })}>
        <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
        <span dir="ltr" className="font-mono tabular-nums">−{fmtQty(periodOut, i18n.language)}</span>
      </span>
      <span className="text-fg-subtle">{t('profile.stock.lastDays', { defaultValue: 'last {{days}} days', days })}</span>
    </div>
  );
}

/** Card wrapper for a tab's error state. */
export function LoadError(): JSX.Element {
  const { t } = useTranslation('items');
  return <Card className="p-6"><p className="text-sm text-danger">{t('detail.loadError', { defaultValue: 'Could not load.' })}</p></Card>;
}
